import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { ArrowRight, Loader2 } from 'lucide-react';
import { FloatingAgentAvatar } from '@/components/FloatingAgentAvatar';

export default function HomePage() {
  const { user, currentProject } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [agentWork, setAgentWork] = useState([]);
  const [activeAgents, setActiveAgents] = useState([]);
  const [events, setEvents] = useState([]);
  const [hotContacts, setHotContacts] = useState([]);
  const [boards, setBoards] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [suggestionsRes, dashRes, eventsRes, contactsRes, boardsRes, approvalRes, phaseRes, orchestraRes] = await Promise.allSettled([
          api.post('/system/proactive-scan', {}),
          api.get('/dashboard/summary'),
          api.get('/events'),
          api.get('/contacts?limit=5&sort=score'),
          api.get('/process/boards'),
          api.get('/approval-queue?status=pending'),
          api.get('/phase/status'),
          api.get('/agents/orchestra-status'),
        ]);

        // Alerts from proactive suggestions
        if (suggestionsRes.status === 'fulfilled') {
          const sugs = suggestionsRes.value?.data?.suggestions || suggestionsRes.value?.data?.alerts || [];
          const alertItems = sugs.filter(s => s.severity !== 'low').slice(0, 4).map((s, i) => {
            const ageH = s.age_hours || s.hours_ago;
            let timeLabel = '';
            if (ageH != null) {
              timeLabel = ageH < 1 ? 'ora' : ageH < 24 ? `${Math.round(ageH)}h fa` : `${Math.round(ageH / 24)}gg fa`;
            } else if (s.created_at) {
              const diff = Math.round((Date.now() - new Date(s.created_at)) / 3600000);
              timeLabel = diff < 1 ? 'ora' : diff < 24 ? `${diff}h fa` : `${Math.round(diff / 24)}gg fa`;
            }
            const actionText = s.suggested_action || s.action?.split('.')[0] || 'Vai';
            // Determine link based on context
            let link = s.link || '/board';
            if (s.entity_type === 'event' || s.area === 'events') link = '/events';
            else if (s.entity_type === 'content' || s.area === 'content') link = '/approval';
            else if (s.entity_type === 'contact' || s.area === 'sales' || s.area === 'crm') link = '/contacts';
            else if (s.entity_type === 'deal' || s.area === 'pipeline') link = '/sales';
            return {
              id: i, type: s.severity === 'high' ? 'danger' : 'warning',
              text: s.title || s.message, action: actionText, link, time: timeLabel,
            };
          });
          setAlerts(alertItems);
        }

        // KPIs from dashboard + thresholds from phase/status
        if (dashRes.status === 'fulfilled') {
          const d = dashRes.value?.data || {};
          const areas = phaseRes.status === 'fulfilled' ? (phaseRes.value?.data?.area_levels || {}) : {};
          const salesArea = areas.sales || {};
          const eventsArea = areas.events || {};
          const partnerArea = areas.partnership || {};
          setKpis([
            {
              label: 'Contatti', value: String(d.contacts_count || 0), trend: '', target: 100, current: d.contacts_count || 0,
              threshold: salesArea.next_level_name ? { value: salesArea.next_level_name, label: salesArea.next_unlock_tip || '', pct: Math.round(salesArea.progress_to_next || 0) } : null,
            },
            {
              label: 'Eventi', value: String(d.events_count || 0), trend: '', target: 20, current: d.events_count || 0,
              threshold: eventsArea.next_level_name ? { value: eventsArea.next_level_name, label: eventsArea.next_unlock_tip || '', pct: Math.round(eventsArea.progress_to_next || 0) } : null,
            },
            {
              label: 'Pipeline deals', value: String(d.deals_count || 0), trend: '', target: 50, current: d.deals_count || 0,
              threshold: partnerArea.next_level_name ? { value: partnerArea.next_level_name, label: partnerArea.next_unlock_tip || '', pct: Math.round(partnerArea.progress_to_next || 0) } : null,
            },
            { label: 'Da approvare', value: String(approvalRes.status === 'fulfilled' ? (approvalRes.value?.data?.length || 0) : 0), trend: '', target: 0, current: 0, threshold: null },
          ]);
        }

        // Agent activity from boards + orchestra status
        if (boardsRes.status === 'fulfilled') {
          const running = (boardsRes.value?.data || []).filter(b => ['running', 'paused'].includes(b.status));
          setBoards(running.slice(0, 3));
        }

        // Active agents from orchestra-status
        if (orchestraRes.status === 'fulfilled') {
          const allAgents = orchestraRes.value?.data?.agents || [];
          const active = allAgents.filter(a => a.is_active);
          setActiveAgents(active.slice(0, 8).map(a => {
            const name = (a.agent_name || '').replace('_agent', '').replace('_', ' ');
            return {
              initials: name.replace(/\s/g, '').slice(0, 2).toUpperCase(),
              name: name,
              agent_name: a.agent_name,
            };
          }));

          // Agent work outputs (agents with recent activity)
          const recentWorkers = active
            .filter(a => a.items_today > 0 || a.last_action)
            .slice(0, 6);
          const AGENT_DESTINATIONS = {
            copywriter: { link: '/contents', dest: 'Content Factory' },
            planner: { link: '/editorial', dest: 'Piano editoriale' },
            strategist: { link: '/command', dest: 'Centro di Comando' },
            funnel: { link: '/contacts', dest: 'Contatti & CRM' },
            sales: { link: '/sales', dest: 'Pipeline vendite' },
            scout: { link: '/contacts', dest: 'Contatti & CRM' },
            designer: { link: '/contents', dest: 'Content Factory' },
            operator: { link: '/command', dest: 'Centro di Comando' },
            outreach_executor: { link: '/contents', dest: 'Content Factory' },
            analyst: { link: '/home', dest: 'Dashboard' },
            partnership: { link: '/network', dest: 'Network' },
            evento: { link: '/events', dest: 'Eventi' },
            ads_monitor: { link: '/campaigns', dest: 'Campagne' },
          };
          // Readable action type map (fallback for raw trigger names)
          const readableAction = {
            'bus_dispatch': 'Elaborazione messaggi',
            'content_recycling': 'Riciclo contenuti evento',
            'product_deleted': 'Gestione catalogo prodotti',
            'product_created': 'Nuovo prodotto registrato',
            'F3': 'Newsletter generata',
            'F1': 'Post LinkedIn generato',
            'F2': 'Post aziendale generato',
            'F4': 'Post Instagram generato',
            'F5': 'Email commerciale generata',
            'F6': 'Recap evento generato',
            'R1': 'Post leadership AI',
            'R2': 'Post innovazione PMI',
            'R3': 'Caso studio generato',
            'R4': 'Post community engagement',
            'R5': 'Newsletter settimanale',
            'R6': 'Analisi trend generata',
            'content_generated': 'Contenuto generato',
            'content_published': 'Contenuto pubblicato',
            'contact_enriched': 'Contatto arricchito',
            'event_promo': 'Promozione evento',
            'deal_updated': 'Deal aggiornato',
            'newsletter_sent': 'Newsletter inviata',
            'plan_generated': 'Piano generato',
            'strategy_updated': 'Strategia aggiornata',
            'partnership_scored': 'Partnership valutata',
          };
          const getReadableOutput = (agent) => {
            // Priority: action_description > details > output_summary > readable map of action > raw
            if (agent.action_description) return agent.action_description;
            if (agent.details && agent.details !== agent.last_action) return agent.details;
            if (agent.last_output_summary) return agent.last_output_summary;
            if (agent.status_message && agent.status_message !== 'idle') return agent.status_message;
            const action = agent.last_action || '';
            if (readableAction[action]) return readableAction[action];
            // If it looks like a raw trigger, provide generic readable text
            if (action.includes('_') || action.length <= 3) return readableAction[action] || 'Elaborazione in corso';
            return action;
          };
          setAgentWork(recentWorkers.map(a => {
            const shortName = (a.agent_name || '').replace('_agent', '');
            const dest = AGENT_DESTINATIONS[shortName] || { link: '/command', dest: 'Centro di Comando' };
            return {
              initials: shortName.replace('_', '').slice(0, 2).toUpperCase(),
              name: shortName.replace('_', ' '),
              agent_name: a.agent_name,
              output: getReadableOutput(a),
              items: a.items_today || 0,
              link: dest.link,
              destination: dest.dest,
            };
          }));
        }

        // Events
        if (eventsRes.status === 'fulfilled') {
          setEvents((eventsRes.value?.data || []).slice(0, 3));
        }

        // Hot contacts
        if (contactsRes.status === 'fulfilled') {
          setHotContacts((contactsRes.value?.data || []).slice(0, 4));
        }
      } catch (e) { console.error('Home load error:', e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const firstName = user?.full_name?.split(' ')[0] || 'Emanuele';
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const dateStr = now.toLocaleDateString('it-CH', { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="home-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-6 overflow-auto p-6" data-testid="home-page" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Main Column - 70% */}
      <div className="flex w-[70%] flex-col gap-6">
        {/* Greeting */}
        <div>
          <h1 className="text-[22px] font-bold" style={{ color: '#1B2B4B' }} data-testid="home-greeting">
            {greeting} {firstName}
          </h1>
          <p className="text-[14px]" style={{ color: '#7C8CA2' }}>
            {dateStr} {alerts.length > 0 ? `\u2014 ${alerts.length} azioni urgenti` : ''}
          </p>
        </div>

        {/* Alerts — card colorate con azione */}
        {alerts.length > 0 && (
          <div className="flex flex-col gap-3" data-testid="home-alerts">
            {alerts.map(alert => (
              <div key={alert.id}
                className="flex items-center justify-between rounded-xl px-5 py-4 cursor-pointer transition-all hover:shadow-md"
                style={{
                  backgroundColor: alert.type === 'danger' ? '#FEE2E2' : '#FEF3C7',
                  borderLeft: `4px solid ${alert.type === 'danger' ? '#DC2626' : '#D97706'}`,
                }}
                onClick={() => navigate(alert.link)}
                data-testid={`alert-${alert.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0"
                    style={{ backgroundColor: alert.type === 'danger' ? '#DC2626' : '#D97706' }}>
                    <span className="text-[14px] text-white font-bold">!</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold truncate" style={{ color: '#1B2B4B' }}>{alert.text}</p>
                    {alert.time && (
                      <span className="text-[11px]" style={{ color: alert.type === 'danger' ? '#991B1B' : '#92400E' }}>{alert.time}</span>
                    )}
                  </div>
                </div>
                <button className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-bold flex-shrink-0 ml-3 transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: alert.type === 'danger' ? '#DC2626' : '#D97706',
                    color: '#FFFFFF',
                  }}
                  onClick={e => { e.stopPropagation(); navigate(alert.link); }}>
                  {alert.action} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* KPIs */}
        <div>
          <h2 className="mb-4 text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
            Indicatori e soglie di crescita
          </h2>
          <div className="grid grid-cols-4 gap-4" data-testid="home-kpis">
            {kpis.map((kpi, i) => {
              const pct = kpi.target > 0 ? Math.min(100, Math.round((kpi.current / kpi.target) * 100)) : 0;
              const barColor = pct >= 70 ? '#059669' : pct >= 40 ? '#D97706' : '#DC2626';
              return (
                <div key={i} className="rounded-lg bg-white p-4" style={{ border: '1px solid #E5E7EB' }}
                  data-testid={`kpi-${i}`}>
                  <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{kpi.label}</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-[24px] font-bold" style={{ color: '#1B2B4B' }}>{kpi.value}</span>
                  </div>
                  {kpi.target > 0 && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                      </div>
                      <div className="mt-1 flex justify-between text-[10px]" style={{ color: '#7C8CA2' }}>
                        <span>{pct}%</span>
                        <span>target {kpi.target}</span>
                      </div>
                      {kpi.threshold && (
                        <div className="mt-2 rounded px-2 py-1" style={{ backgroundColor: '#F3F4F6' }}
                          data-testid={`kpi-threshold-${i}`}>
                          <span className="text-[10px]" style={{ color: '#7C8CA2' }}>
                            Soglia {kpi.threshold.value}: {kpi.threshold.label} ({kpi.threshold.pct}%)
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Agent Activity - "Il tuo team AI ha lavorato stanotte" */}
        {(agentWork.length > 0 || activeAgents.length > 0) && (
          <div>
            <div className="mb-4">
              <h2 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
                Il tuo team AI ha lavorato stanotte
              </h2>
              {/* Mini avatars row */}
              {activeAgents.length > 0 && (
                <div className="mt-2 flex items-center gap-1" data-testid="agent-mini-avatars">
                  {activeAgents.map((a, i) => (
                    <div key={i}
                      className="flex h-[24px] w-[24px] items-center justify-center rounded-full text-[9px] font-bold text-white"
                      style={{ backgroundColor: '#2563EB' }}
                      title={a.name}
                      data-testid={`mini-avatar-${i}`}>
                      {a.initials}
                    </div>
                  ))}
                  <span className="ml-2 text-[11px]" style={{ color: '#7C8CA2' }}>
                    {activeAgents.length} agenti attivi
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2" data-testid="home-agents">
              {agentWork.map((a, i) => (
                <button key={i}
                  onClick={() => navigate(a.link)}
                  className="flex w-full items-center gap-3 rounded-lg bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB' }}
                  data-testid={`agent-chip-${i}`}>
                  <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0"
                    style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                    {a.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px]" style={{ color: '#1B2B4B' }}>
                      <strong className="capitalize">{a.name}:</strong> {a.output}
                    </span>
                    <span className="ml-2 text-[11px] font-medium" style={{ color: '#2563EB' }}>
                      &rarr; {a.destination}
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-shrink-0" style={{ color: '#7C8CA2' }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Column - 30% */}
      <div className="flex w-[30%] flex-col gap-6">
        {/* Events */}
        <div className="rounded-lg bg-white p-4" style={{ border: '1px solid #E5E7EB' }}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
              Prossimi eventi
            </h3>
            <button onClick={() => navigate('/events')} className="text-[12px] font-medium" style={{ color: '#2563EB' }}>
              Tutti
            </button>
          </div>
          {events.length === 0 ? (
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun evento in programma</p>
          ) : (
            <div className="space-y-3" data-testid="home-events">
              {events.map(ev => (
                <div key={ev.id} className="flex items-center justify-between py-1 cursor-pointer hover:bg-gray-50 rounded px-1"
                  onClick={() => navigate('/events')} data-testid={`event-${ev.id}`}>
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{ev.title || ev.name}</p>
                    <p className="text-[11px]" style={{ color: '#7C8CA2' }}>
                      {ev.date ? new Date(ev.date).toLocaleDateString('it-CH', { day: 'numeric', month: 'short' }) : ''}
                      {ev.enrolled !== undefined && ` \u00B7 ${ev.enrolled}/${ev.target || '?'} iscritti`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Hot Contacts */}
        <div className="rounded-lg bg-white p-4" style={{ border: '1px solid #E5E7EB' }}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
              Contatti caldi
            </h3>
            <button onClick={() => navigate('/contacts')} className="text-[12px] font-medium" style={{ color: '#2563EB' }}>
              CRM
            </button>
          </div>
          {hotContacts.length === 0 ? (
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun contatto caldo</p>
          ) : (
            <div className="space-y-2" data-testid="home-contacts">
              {hotContacts.map(c => (
                <div key={c.id} className="flex items-center justify-between py-1" data-testid={`contact-${c.id}`}>
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{c.full_name || c.name}</p>
                    <p className="text-[11px]" style={{ color: '#7C8CA2' }}>
                      {c.company || ''} {c.stage ? `\u00B7 ${c.stage}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Boards */}
        <div className="rounded-lg bg-white p-4" style={{ border: '1px solid #E5E7EB' }}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
              Processi attivi
            </h3>
            <button onClick={() => navigate('/board')} className="text-[12px] font-medium" style={{ color: '#2563EB' }}>
              Board
            </button>
          </div>
          {boards.length === 0 ? (
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun processo attivo</p>
          ) : (
            <div className="space-y-3" data-testid="home-boards">
              {boards.map(b => (
                <div key={b.id} className="cursor-pointer hover:bg-gray-50 rounded px-1 py-1"
                  onClick={() => navigate(`/board?board_id=${b.id}`)} data-testid={`board-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{b.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.status === 'paused' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <FloatingAgentAvatar />
    </div>
  );
}
